import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import UserAvatarImage from '../user-avatar-image';
import QuickUserInfo from '../quick-user-info';
import MemberCardAsync from '../../containers/member-card-async';
import ProfileLink from '../link/profile-link';
import RoleBadge from '../role-badge';
import styles from './avatar.scss';

function Avatar({
  type,
  user = {},
  date,
  showDate,
  showBadges,
  truncateBadgeTitle,
  showMemberCard,
  showUserInfoInline,
  hideMetaData,
  prefix = '',
}) {
  const className = classNames(
    styles.container,
    'forum-text-color',
    { [styles[type]]: type },
    'avatar',
  );

  const wrapWithProfileLink = (component) => (
    <ProfileLink user={user} className={styles.link}>
      {component}
    </ProfileLink>
  );

  const wrapWithMemberCard = (component) => (
    <MemberCardAsync
      viewedMemberId={user.siteMemberId}
      viewedMemberSlug={user.slug}
      className={styles.linkWrapper}
    >
      {component}
    </MemberCardAsync>
  );

  const avatarImage = <UserAvatarImage type={type} user={user} className={styles.noShrink} />;
  const userName = (
    <span
      className={classNames(styles.name, 'forum-text-color', 'forum-link-hover-color')}
      data-hook="avatar__name"
    >
      {prefix ? `${prefix} ${user?.name}` : user?.name}
    </span>
  );
  const roleBadge = (
    <RoleBadge user={user} className={classNames('forum-icon-fill', styles.noShrink)} />
  );

  const renderUserInfo = () => (
    <QuickUserInfo
      user={user}
      date={date}
      showDate={showDate}
      showBadges={showBadges}
      truncateBadgeTitle={truncateBadgeTitle}
      showFirstSeparator={showUserInfoInline}
      noMargin={showUserInfoInline}
      className={classNames(styles.quickUserInfo, { [styles[type]]: type })}
    />
  );

  const content = (
    <div className={className}>
      {avatarImage}
      <div className={styles.profileInfo}>
        <div className={styles.userName}>
          <div className={styles.user}>
            {userName}
            {showUserInfoInline && !hideMetaData && (
              <React.Fragment>
                {roleBadge}
                {renderUserInfo()}
              </React.Fragment>
            )}
          </div>
          {!showUserInfoInline && !hideMetaData && roleBadge}
        </div>
        {!showUserInfoInline && !hideMetaData && renderUserInfo()}
      </div>
    </div>
  );

  return showMemberCard
    ? wrapWithMemberCard(wrapWithProfileLink(content))
    : wrapWithProfileLink(content);
}

Avatar.SMALL = 'small';
Avatar.LARGE = 'large';
Avatar.MEDIUM = 'medium';
Avatar.FOOTER_POST = 'footerPost';
Avatar.POST_PAGE = 'postPage';
Avatar.RECENT_POSTS_WIDGET = 'recentPostsWidget';
Avatar.WHO_LIKED = 'whoLiked';
Avatar.POST_PAGE_HEADER = 'postPageHeader';

Avatar.propTypes = {
  user: PropTypes.object.isRequired,
  type: PropTypes.string,
  date: PropTypes.string,
  showDate: PropTypes.bool,
  showBadges: PropTypes.bool,
  compact: PropTypes.bool,
  showMemberCard: PropTypes.bool,
  showUserInfoInline: PropTypes.bool,
};

Avatar.defaultProps = {
  showBadges: true,
  showMemberCard: true,
  showInlineBadges: false,
};

export default Avatar;
